/**
 * Blockchain-feed response → canonical rows + graph elements.
 *
 * The feed lambda returns per-chain blocks of `{ transactions, unique_addresses,
 * graph }` (GET) or the same nested under `addresses[]` (POST). This adapter is
 * a thin re-shape over the SHARED normalizers so the Overview widget never forks
 * field names or graph mapping:
 *   - rows  → `coerceTransactionRow` (`@authe/fe-commons` via `@authe/tx-table`):
 *             one place that reconciles `hash`/`transaction_hash` → `tx_hash`,
 *             `from_address` → `from`, the `{ transaction, … }` envelope, etc.
 *   - graph → `toGraph` (`@authe/tx-graph`): backend `InteractionGraph` →
 *             React Flow elements, already multichain-aware (per-node `chain_id`,
 *             off-chain `node_type` badges, cross-chain edges).
 *
 * The only feed-specific logic here is merging the per-chain graphs into one
 * multichain `InteractionGraph` (stamping each node's chain so colours/badges
 * stay correct) and building the from/to/entity enrichment overlay the table's
 * forensics columns read.
 */
import { coerceTransactionRow, type TxRow, type TxRowEnrichment } from "@authe/tx-table";
import { toGraph, type GraphElements, type ToGraphOptions } from "@authe/tx-graph";
import type { InteractionGraphDTO } from "@authe/fe-commons/models";

interface FeedGraph {
  nodes?: Record<string, unknown>[];
  edges?: Record<string, unknown>[];
  node_count?: number;
  edge_count?: number;
  chain_ids?: string[];
  multi_chain?: boolean;
}

interface FeedChain {
  chain_id?: string;
  transactions?: unknown[];
  unique_addresses?: string[];
  graph?: FeedGraph;
  error?: string;
}

export interface OnchainFeedResponse {
  /** GET (block feed) shape. */
  chains?: FeedChain[];
  /** POST (address feed) shape. */
  addresses?: { address: string; chains?: FeedChain[] }[];
  /**
   * Flat list shape (no `graph: true`): each entry is the `{ transaction,
   * unique_addresses }` envelope `coerceTransactionRow` understands, with a
   * sibling `chain_id`. Returned by the address feed when only rows are needed.
   */
  items?: { chain_id?: string; transaction?: unknown; unique_addresses?: string[] }[];
  /** Optional backend-merged multichain graph (preferred when present). */
  graph?: FeedGraph;
  metadata?: Record<string, unknown>;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

/** Flatten GET `chains` / POST `addresses[].chains` into one chain list. */
function feedChains(feed: OnchainFeedResponse): FeedChain[] {
  if (Array.isArray(feed.addresses)) return feed.addresses.flatMap((a) => a.chains ?? []);
  return feed.chains ?? [];
}

/**
 * Build the forensics enrichment overlay the table reads (`getRowEnrichment`).
 * Derived purely from fields the coerced row already carries — no recompute.
 */
function withEnrichment(row: TxRow): TxRow {
  const r = row as Record<string, unknown>;
  const from = str(r.from) ?? str(r.from_address);
  const to = str(r.to) ?? str(r.to_address);
  const entityName = str(r.to_entity_name) ?? str(r.to_address_label);
  const input = r.input;
  const enrichment: TxRowEnrichment = {
    from,
    to,
    entity: entityName ? { id: to ?? entityName, name: entityName } : null,
    contractInteraction: typeof input === "string" && input.length > 2 && input !== "0x",
  };
  return { ...row, enrichment } as TxRow;
}

/** Normalize every feed transaction into a canonical, enriched `TxRow`. */
export function feedRows(feed: OnchainFeedResponse | undefined): TxRow[] {
  if (!feed) return [];
  const rows: TxRow[] = [];

  // Flat list shape (`result.items[]`) takes precedence: each entry is the
  // `{ transaction, unique_addresses }` envelope coerceTransactionRow unwraps,
  // with a sibling `chain_id` to stamp when the inner tx omits one.
  if (Array.isArray(feed.items)) {
    for (const item of feed.items) {
      const row = coerceTransactionRow(item as Record<string, unknown>);
      if (!row.chain_id && item.chain_id) (row as { chain_id: string }).chain_id = item.chain_id;
      rows.push(withEnrichment(row));
    }
    return rows;
  }

  for (const chain of feedChains(feed)) {
    for (const entry of chain.transactions ?? []) {
      const row = coerceTransactionRow(entry as Record<string, unknown>);
      // Fall back to the chain's id when the tx itself didn't carry one.
      if (!row.chain_id && chain.chain_id) (row as { chain_id: string }).chain_id = chain.chain_id;
      rows.push(withEnrichment(row));
    }
  }
  return rows;
}

/** Stamp a chain id onto nodes that didn't declare one (keeps merges chain-aware). */
function stampChain(graph: FeedGraph, chainId: string | undefined): {
  nodes: Record<string, unknown>[];
  edges: Record<string, unknown>[];
} {
  const nodes = (graph.nodes ?? []).map((n) =>
    (n as { chain_id?: unknown }).chain_id == null && chainId ? { ...n, chain_id: chainId } : n,
  );
  return { nodes, edges: graph.edges ?? [] };
}

/**
 * Resolve the feed's interaction graph: prefer a backend-merged top-level graph;
 * otherwise merge the per-chain graphs into one multichain `InteractionGraph`
 * (deduping nodes by id, stamping each node's chain).
 */
export function feedGraph(feed: OnchainFeedResponse | undefined): InteractionGraphDTO {
  if (!feed) return { nodes: [], edges: [] } as unknown as InteractionGraphDTO;
  if (feed.graph && ((feed.graph.nodes?.length ?? 0) > 0 || (feed.graph.edges?.length ?? 0) > 0)) {
    return feed.graph as unknown as InteractionGraphDTO;
  }

  const nodes: Record<string, unknown>[] = [];
  const edges: Record<string, unknown>[] = [];
  const seenNodes = new Set<string>();
  const chainIds = new Set<string>();

  for (const chain of feedChains(feed)) {
    if (chain.chain_id) chainIds.add(chain.chain_id);
    if (!chain.graph) continue;
    const stamped = stampChain(chain.graph, chain.chain_id);
    for (const node of stamped.nodes) {
      const id = String((node as { id?: unknown; address?: unknown }).id ?? (node as { address?: unknown }).address ?? "");
      if (id) {
        if (seenNodes.has(id)) continue;
        seenNodes.add(id);
      }
      nodes.push(node);
    }
    for (const edge of stamped.edges) edges.push(edge);
  }

  return {
    nodes,
    edges,
    node_count: nodes.length,
    edge_count: edges.length,
    chain_ids: [...chainIds],
    multi_chain: chainIds.size > 1,
  } as unknown as InteractionGraphDTO;
}

/** Feed → React Flow elements (multichain-aware) ready for `<GraphCanvas>`. */
export function feedToGraph(
  feed: OnchainFeedResponse | undefined,
  options?: ToGraphOptions,
): GraphElements {
  return toGraph(feedGraph(feed), options);
}
